import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type {Column, RowData, Table as TanStackTable} from "@tanstack/react-table";
import {useTranslation} from "react-i18next";
import {SERVER_TABLE_PANEL_WIDTH} from "./constants";

type SortDirection = 'asc' | 'desc';

interface SortPanelProps<TData extends RowData> {
    table: TanStackTable<TData>
    onClose?: () => void
}

const SortPanel = <TData extends RowData>({table, onClose}: SortPanelProps<TData>) => {
    const {t} = useTranslation();
    const sortableColumns = table.getAllLeafColumns()
        .filter(column => column.columnDef.meta?.isServerSortable === true);
    const activeSort = table.getState().sorting[0];
    const activeDirection: SortDirection = activeSort?.desc ? 'desc' : 'asc';

    const labelFor = (column: Column<TData, unknown>) => (
        typeof column.columnDef.header === 'string'
            ? t(column.columnDef.header)
            : column.columnDef.meta?.headerLabel ?? column.id
    );
    const clearSort = () => table.setSorting([]);
    const setSort = (columnId: string, direction: SortDirection) => {
        table.setSorting([{id: columnId, desc: direction === 'desc'}]);
    };
    const addSort = () => {
        const firstColumn = sortableColumns[0];
        if (firstColumn) setSort(firstColumn.id, 'asc');
    };

    return (
        <Stack sx={{width: SERVER_TABLE_PANEL_WIDTH, minWidth: 0}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 2, pb: 1, flexShrink: 0}}>
                <Typography variant="body1" fontWeight={500}>
                    {t('Sort')}
                </Typography>

                {onClose && (
                    <IconButton size="small" aria-label={t('Close')} onClick={onClose} sx={{ml: 'auto'}}>
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                )}
            </Box>

            <Box sx={{px: 2, pb: 2, pt: 2}}>
                {!activeSort ? (
                    <Button
                        type="button"
                        size="small"
                        color="inherit"
                        startIcon={<AddIcon/>}
                        disabled={!sortableColumns.length}
                        onClick={addSort}
                        sx={{justifyContent: 'flex-start', textTransform: 'none'}}
                    >
                        {t('Add sort')}
                    </Button>
                ) : (
                    <Stack gap={0.75}>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'minmax(0, 1fr) 112px',
                                alignItems: 'center',
                                gap: 0.5,
                                minWidth: 0
                            }}
                        >
                            <Select
                                size="small"
                                value={activeSort.id}
                                onChange={event => setSort(String(event.target.value), activeDirection)}
                                inputProps={{'aria-label': t('Sort property')}}
                                sx={{minWidth: 0, height: 32}}
                            >
                                {sortableColumns.map(column => (
                                    <MenuItem key={column.id} value={column.id}>{labelFor(column)}</MenuItem>
                                ))}
                            </Select>

                            <Select
                                size="small"
                                value={activeDirection}
                                onChange={event => setSort(activeSort.id, event.target.value as SortDirection)}
                                inputProps={{'aria-label': t('Sort direction')}}
                                sx={{height: 32}}
                            >
                                <MenuItem value="asc">{t('Ascending')}</MenuItem>
                                <MenuItem value="desc">{t('Descending')}</MenuItem>
                            </Select>

                        </Box>

                        <Button
                            type="button"
                            size="small"
                            color="error"
                            startIcon={<DeleteOutlineIcon/>}
                            onClick={clearSort}
                            sx={{alignSelf: 'flex-start', textTransform: 'none'}}
                        >
                            {t('Delete sort')}
                        </Button>
                    </Stack>
                )}
            </Box>
        </Stack>
    );
};

export default SortPanel;
